import { type CatalogGroupDto, DEMAND_TRIGGERS, type DemandTrigger, type MeDto, type WarehouseDto, warehouseInputSchema, type WarehouseSettings } from '@bqcrm/shared';
import { Alert, Badge, Button, Card, Group, Modal, NumberInput, Select, SimpleGrid, Stack, Switch, Table, Text, Textarea, TextInput } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, errorText } from '../../api';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { useCatalogGroups, useCompanies, useWarehouses, useWarehouseSettings } from '../../data';
import { notifyError, notifyOk } from '../../notify';

/** Ustawienia → Magazyny: magazyny firm, grupy produktów, próg nieaktualnej ceny zakupu. */
export function WarehouseSettingsPage(_: { me: MeDto }) {
  const whs = useWarehouses();
  const groups = useCatalogGroups();
  const companies = useCompanies();
  const [editWh, setEditWh] = useState<WarehouseDto | 'new' | null>(null);
  const [editGroup, setEditGroup] = useState<CatalogGroupDto | 'new' | null>(null);
  return (
    <>
      <PageHeader title="Magazyny i grupy produktów" description="Każdy magazyn należy do jednej firmy (właściciel towaru). Grupy porządkują urządzenia i materiały w katalogu i na stanach." />
      <Stack>
        <SectionCard
          title="Magazyny"
          actions={
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setEditWh('new')}>
              Dodaj magazyn
            </Button>
          }
          padding={0}
        >
          <QueryState isLoading={whs.isLoading} error={whs.error}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Firma</Table.Th>
                  <Table.Th>Kod</Table.Th>
                  <Table.Th>Nazwa / adres</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {whs.data?.map((w) => (
                  <Table.Tr key={w.id} className="row-link" onClick={() => setEditWh(w)}>
                    <Table.Td>{companies.data?.find((c) => c.id === w.companyId)?.shortName ?? w.companyCode}</Table.Td>
                    <Table.Td>{w.code}</Table.Td>
                    <Table.Td>
                      <Text size="sm">{w.name}</Text>
                      <Text size="xs" c="dimmed">
                        {w.address || '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {w.isDefault && (
                          <Badge size="sm" variant="light">
                            Domyślny
                          </Badge>
                        )}
                        {!w.active && (
                          <Badge size="sm" variant="light" color="gray">
                            Nieaktywny
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </QueryState>
        </SectionCard>
        <SectionCard
          title="Grupy produktów"
          actions={
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setEditGroup('new')}>
              Dodaj grupę
            </Button>
          }
          padding={0}
        >
          <QueryState isLoading={groups.isLoading} error={groups.error}>
            {groups.data?.length === 0 ? (
              <EmptyState compact title="Brak grup" hint="Np. pompy ciepła, klimatyzatory, falowniki, rury, kształtki, przewody, uchwyty, zabezpieczenia." />
            ) : (
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Nazwa</Table.Th>
                    <Table.Th ta="right">Produktów</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {groups.data?.map((g) => (
                    <Table.Tr key={g.id} className="row-link" onClick={() => setEditGroup(g)}>
                      <Table.Td>{g.name}</Table.Td>
                      <Table.Td ta="right">{g.itemCount}</Table.Td>
                      <Table.Td>{!g.active && <Badge size="sm" color="gray" variant="light">Nieaktywna</Badge>}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </QueryState>
        </SectionCard>
        <PriceSettings />
      </Stack>
      {editWh && <WarehouseModal wh={editWh === 'new' ? null : editWh} onClose={() => setEditWh(null)} />}
      {editGroup && <GroupModal group={editGroup === 'new' ? null : editGroup} onClose={() => setEditGroup(null)} />}
    </>
  );
}

function WarehouseModal({ wh, onClose }: { wh: WarehouseDto | null; onClose: () => void }) {
  const companies = useCompanies();
  const [f, setF] = useState({
    companyId: wh?.companyId ?? '',
    code: wh?.code ?? '',
    name: wh?.name ?? '',
    address: wh?.address ?? '',
    pickupNote: wh?.pickupNote ?? '',
    active: wh?.active ?? true,
    isDefault: wh?.isDefault ?? false,
  });
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => {
      const r = warehouseInputSchema.safeParse(f);
      if (!r.success) throw new Error([...new Set(r.error.issues.map((i) => i.message))].join('\n'));
      return wh ? api(`/warehouses/${wh.id}`, { method: 'PUT', body: r.data }) : api('/warehouses', { method: 'POST', body: r.data });
    },
    onSuccess: () => {
      notifyOk(wh ? 'Zapisano magazyn' : 'Dodano magazyn');
      void qc.invalidateQueries({ queryKey: ['warehouses'] });
      onClose();
    },
    onError: (e) => setError(errorText(e)),
  });
  return (
    <Modal opened onClose={onClose} title={wh ? 'Edycja magazynu' : 'Nowy magazyn'}>
      <Stack>
        <Select label="Firma (właściciel towaru)" data={(companies.data ?? []).map((c) => ({ value: c.id, label: c.shortName }))} value={f.companyId || null} onChange={(v) => v && setF({ ...f, companyId: v })} allowDeselect={false} />
        <SimpleGrid cols={2}>
          <TextInput label="Kod" value={f.code} onChange={(e) => setF({ ...f, code: e.currentTarget.value.toUpperCase() })} />
          <TextInput label="Nazwa" value={f.name} onChange={(e) => setF({ ...f, name: e.currentTarget.value })} />
        </SimpleGrid>
        <TextInput label="Adres" value={f.address} onChange={(e) => setF({ ...f, address: e.currentTarget.value })} />
        <Textarea label="Miejsce odbioru dla ekip" description="Brama, regał wydań, godziny — widzą to ekipy przy pakietach." autosize minRows={2} value={f.pickupNote} onChange={(e) => setF({ ...f, pickupNote: e.currentTarget.value })} />
        <Group>
          <Switch label="Domyślny magazyn firmy" checked={f.isDefault} onChange={(e) => setF({ ...f, isDefault: e.currentTarget.checked })} />
          <Switch label="Aktywny" checked={f.active} onChange={(e) => setF({ ...f, active: e.currentTarget.checked })} />
        </Group>
        {error && (
          <Alert color="red" style={{ whiteSpace: 'pre-line' }}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function GroupModal({ group, onClose }: { group: CatalogGroupDto | null; onClose: () => void }) {
  const [name, setName] = useState(group?.name ?? '');
  const [sortOrder, setSortOrder] = useState(group?.sortOrder ?? 0);
  const [active, setActive] = useState(group?.active ?? true);
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => (group ? api(`/catalog/groups/${group.id}`, { method: 'PUT', body: { name, sortOrder, active } }) : api('/catalog/groups', { method: 'POST', body: { name, sortOrder, active } })),
    onSuccess: () => {
      notifyOk(group ? 'Zapisano grupę' : 'Dodano grupę');
      void qc.invalidateQueries({ queryKey: ['catalog-groups'] });
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={group ? 'Edycja grupy produktów' : 'Nowa grupa produktów'}>
      <Stack>
        <TextInput label="Nazwa" value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
        <NumberInput label="Kolejność" min={0} max={9999} allowDecimal={false} value={sortOrder} onChange={(v) => setSortOrder(Number(v) || 0)} />
        <Switch label="Aktywna" checked={active} onChange={(e) => setActive(e.currentTarget.checked)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={name.trim().length < 2}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function PriceSettings() {
  const s = useWarehouseSettings();
  const companies = useCompanies();
  const [f, setF] = useState<WarehouseSettings>({ priceStaleDays: 180, demandTriggers: {}, pickupLeadDays: 1 });
  useEffect(() => {
    if (s.data) setF(s.data);
  }, [s.data]);
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => api<WarehouseSettings>('/warehouse-settings', { method: 'PUT', body: f }),
    onSuccess: () => {
      notifyOk('Zapisano ustawienia magazynu');
      void qc.invalidateQueries({ queryKey: ['warehouse-settings'] });
      void qc.invalidateQueries({ queryKey: ['catalog'] });
    },
    onError: notifyError,
  });
  const dirty = JSON.stringify(f) !== JSON.stringify(s.data);
  return (
    <SectionCard title="Zapotrzebowania i ceny">
      <Stack>
        <Text size="sm" fw={600}>
          Kiedy powstają potrzeby materiałowe z umowy
        </Text>
        {(companies.data ?? []).map((c) => (
          <Select
            key={c.id}
            label={c.shortName}
            data={Object.entries(DEMAND_TRIGGERS).map(([value, label]) => ({ value, label }))}
            value={f.demandTriggers[c.id] ?? 'podpis'}
            onChange={(v) => v && setF({ ...f, demandTriggers: { ...f.demandTriggers, [c.id]: v as DemandTrigger } })}
            allowDeselect={false}
            maw={520}
          />
        ))}
        <Text size="xs" c="dimmed">
          Potrzeby rezerwują tylko wolny towar (nie odbierają innym). Podpisane wcześniej umowy można uzupełnić ręcznie w sprawie → Materiały.
        </Text>
        <Group align="flex-end" wrap="wrap">
          <NumberInput
            label="Materiał gotowy do odbioru (dni roboczych przed realizacją)"
            min={0}
            max={10}
            allowDecimal={false}
            value={f.pickupLeadDays}
            onChange={(v) => setF({ ...f, pickupLeadDays: Number(v) || 0 })}
            w={{ base: '100%', sm: 360 }}
          />
          <NumberInput
            label="Cena zakupu nieaktualna po (dniach)"
            description="Ostrzeżenie przy produkcie i w kalkulacji oferty."
            min={7}
            max={3650}
            allowDecimal={false}
            value={f.priceStaleDays}
            onChange={(v) => setF({ ...f, priceStaleDays: Number(v) || 180 })}
            w={{ base: '100%', sm: 360 }}
          />
        </Group>
        <Group justify="flex-end">
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </SectionCard>
  );
}
